/**
 * 地図手書きメモのモデレーション panel (docs/24 P5b、admin)。
 *
 * 担当地区の手書きメモ一覧を表示し、不適切なメモを非表示 / 削除できる。
 * 非表示 (archivedAt + hidden_all) はユーザーアプリの共有表示から即時に消える。
 */
import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { useMapMemoModeration } from './useMapMemoModeration';

export interface MapMemoModerationPanelProps {
  districtId: string;
  uid: string;
  canEdit: boolean;
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  return new Date(t).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MapMemoModerationPanel({ districtId, uid, canEdit }: MapMemoModerationPanelProps) {
  const { state, archive, restore, remove } = useMapMemoModeration(districtId, uid);
  const { toast } = useToast();

  async function run(fn: () => Promise<void>, ok: string) {
    try {
      await fn();
      toast('success', ok);
    } catch (e) {
      toast('error', `失敗: ${(e as Error).message}`);
    }
  }

  return (
    <Card padding="lg">
      <h2 className="mb-1 text-base font-bold text-slate-900">🖊 手書きメモのモデレーション</h2>
      <p className="mb-3 text-xs text-slate-600">
        ユーザーが地図に描いた手書きメモの一覧です。不適切なものは「非表示」で共有から除外、
        「削除」で完全に消去できます (docs/24)。非表示は復元できます。
      </p>

      {state.status === 'loading' && <p className="text-sm text-slate-500">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-sm text-danger-700">読込エラー: {state.error.message}</p>
      )}
      {state.status === 'ready' && state.memos.length === 0 && (
        <p className="text-sm text-slate-500">まだ手書きメモはありません。</p>
      )}

      {state.status === 'ready' && state.memos.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {state.memos.map((m) => {
            const hidden = m.visibility === 'hidden_all' || m.archivedAt !== null;
            return (
              <li key={m.authorUid} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                <span className="text-sm font-medium text-slate-800">
                  {m.authorNickname || '(匿名)'}
                </span>
                <span className="text-xs text-slate-500">{m.strokeCount} 本</span>
                {m.sessionId && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                    セッション {m.sessionId.slice(0, 6)}
                  </span>
                )}
                <span className="text-xs text-slate-400">{fmtTime(m.updatedAt)}</span>
                {hidden && (
                  <span className="rounded bg-warning-100 px-1.5 py-0.5 text-[11px] text-warning-700">
                    非表示中
                  </span>
                )}
                <span className="ml-auto flex items-center gap-1">
                  {hidden ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!canEdit}
                      onClick={() => void run(() => restore(m.authorUid), 'メモを再共有しました')}
                    >
                      復元
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!canEdit}
                      onClick={() => void run(() => archive(m.authorUid), 'メモを非表示にしました')}
                    >
                      非表示
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={!canEdit}
                    onClick={() => {
                      if (
                        !confirm(
                          `${m.authorNickname || '(匿名)'} の手書きメモを完全に削除しますか?`,
                        )
                      )
                        return;
                      void run(() => remove(m.authorUid), 'メモを削除しました');
                    }}
                  >
                    削除
                  </Button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
